import classNames from 'classnames';
import { ContentState, convertFromRaw, convertToRaw, type EditorState, type RawDraftContentState } from 'draft-js';
import { isEmpty, isEqual, isNil } from 'lodash';
import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { CellTypeNames, TextAlignment } from '../../../../common/table/CellTypeConstants';
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';
import type { CellEditingSession } from '../../../../common/table/TableTypes';

import applyInlineStylesThroughoutContent from '../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';
import TableCellEditor from '../../../components/editor/tableCell/TableCellEditor';
import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';

import {
    getCellEditingAlignment,
    getCellTextAlignment,
    getLocalisedContent,
} from '../utils/dataUtils/cell/tableCellDisplayUtils';
import { getBackgroundColorClass, getCellLevelTextStyles } from '../utils/dataUtils/cell/tableCellStyleUtils';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import { getTableCellVerticalAlignmentClassName } from '../utils/domUtils/tableCellVerticalAlignmentClassNameUtils';
import { getHotTableCellData } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { getTableCellEditorId } from '../utils/tableEditorSessionUtils';
import { MNHotInstance } from '../tableHotInstanceTypes';

type MilanoteCellEditorComponentProps = {
    element: ImMNElement;
    elementId: string;
    isEditable: boolean;
    cellEditingSession: CellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef | null>;
    locale: string;
    gridSize: number;
    mounted: boolean;
};

type TableCellEditorChangeArgs = {
    editorState: EditorState;
};

const MilanoteCellEditorComponent: React.FC<MilanoteCellEditorComponentProps> = ({
    element,
    elementId,
    isEditable,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableContainerRef,
    hotTableInstanceRef,
    cellEditorRef,
    locale,
}) => {
    const hotTableInstance = hotTableInstanceRef.current;
    const cellData =
        hotTableInstance && cellEditingSession
            ? getHotTableCellData(hotTableInstance, cellEditingSession.cellCoords)
            : undefined;
    const verticalAlignment = cellData?.verticalAlignment;
    const cellTextStyle = cellData?.textStyle;

    const [textAlignment, setTextAlignment] = useState<TextAlignment | null>(null);
    const [textStyle, setTextStyle] = useState<string[] | null>(null);

    // Set initial text alignment and styles
    useEffect(() => {
        if (!cellEditingSession) return;

        setTextStyle(Array.from(cellTextStyle || []));

        // For the editor only, we want to ensure that date types are right aligned
        // Even if the initial value is text, the datePicker will be opened with the current date
        const isDateTimeString =
            !isFormula(cellData?.value || null) && cellData?.type?.name === CellTypeNames.DATE_TIME;
        const editorTextAlignment = isDateTimeString ? TextAlignment.RIGHT : getCellTextAlignment(cellData, locale);

        setTextAlignment(editorTextAlignment);
    }, [cellEditingSession, locale]);

    // Get text content to be rendered in the cell editor.
    const textContent = useMemo((): RawDraftContentState | string | null | undefined => {
        // IMPORTANT: Only update the textContent when user starts editing. This is to prevent a bug where text from
        // previously edited cell is still present in the cell editor when editing a different empty cell.
        // - MilanoteEditor has some optimisations that only updates its internal editorState state when isCurrentlyEditingTableCell
        //   is toggled.
        if (!cellEditingSession) return;
        if (!cellData) return null;

        let contentState;

        if (!isNil(cellEditingSession.initialValue)) {
            contentState = ContentState.createFromText(cellEditingSession.initialValue);
        } else if (cellData.draftContent && !isFormula(cellData.value)) {
            contentState = convertFromRaw(cellData.draftContent);
        } else {
            if (isFormula(cellData.value)) return cellData.value;

            const localisedContent = getLocalisedContent(cellData, locale);
            contentState = ContentState.createFromText(localisedContent.toString());
        }

        // Apply cell-level text styling if needed
        if (!isEmpty(cellTextStyle)) {
            contentState = applyInlineStylesThroughoutContent(contentState, cellTextStyle);
        }

        return convertToRaw(contentState);
    }, [cellEditingSession, locale]);

    const backgroundClasses = useMemo(() => getBackgroundColorClass(cellData), [cellEditingSession]);

    const onChange = useCallback(
        ({ editorState }: TableCellEditorChangeArgs) => {
            if (!cellEditingSession) return;

            const currentContent = editorState.getCurrentContent();
            const editCellValue = currentContent.getPlainText();

            // check whether alignment should be updated
            const updateAlignment = cellData ? getCellEditingAlignment(editCellValue, cellData, locale) : undefined;
            if (updateAlignment) setTextAlignment(updateAlignment);

            // update the text style if the user has changed the inline styles
            if (editCellValue.length > 0) {
                const cellLevelTextStyles = getCellLevelTextStyles(currentContent);
                if (!isEqual(cellLevelTextStyles.sort(), textStyle?.sort() || [])) {
                    setTextStyle(cellLevelTextStyles);
                }
            }

            // @ts-ignore - Custom Milanote hook
            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', editorState);
        },
        [cellEditingSession, cellData, locale, textStyle, hotTableInstanceRef],
    );

    const portalTarget = hotTableInstanceRef.current?.rootElement || hotTableContainerRef.current;

    if (!portalTarget) return null;

    return createPortal(
        <div className={classNames('MilanoteCellEditorComponent', backgroundClasses)}>
            <div className="MilanoteCellEditorComponent-relative">
                <div className="handsontable-input-positioner">
                    <div
                        className={classNames(
                            'handsontable-input-holder',
                            getTableCellVerticalAlignmentClassName(verticalAlignment),
                        )}
                    >
                        <TableCellEditor
                            editorRef={cellEditorRef}
                            className="handsontable-input"
                            element={element}
                            isEditing={!!cellEditingSession}
                            isEditable={isEditable}
                            textContent={textContent}
                            defaultStyleOverride={textStyle}
                            editorId={getTableCellEditorId(elementId)}
                            editorKey={TABLE_CELL_EDITOR_KEY}
                            currentEditorId={currentlyEditingEditorId}
                            onChange={onChange}
                            // @ts-ignore - This prop appears unused. But this component is being rebuilt
                            // soon, so will leave as-is for now to avoid unintended consequences.
                            textAlignment={textAlignment}
                        />
                    </div>
                </div>
            </div>
        </div>,
        portalTarget,
    );
};

export default MilanoteCellEditorComponent;
